<script setup lang="ts">
import { computed, defineEmits } from 'vue';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { DrawerBottom, MaterialDetails } from '@ilteducation/components';
import useMaterialCategories from '@/composables/use-material-categories';
import { useLocale } from '@/plugins/translation';

const props = defineProps<{
  material: MaterialV1;
  materialsWithSameOriginalTitle: MaterialV1[];
  isDownloadLoading: boolean;
  isPrintLoading: boolean;
}>();
const emit = defineEmits<{
  (e: 'close'): [];
  (e: 'click-download'): void;
  (e: 'click-print'): void;
  (e: 'click-add-to-collection'): void;
  (e: 'language-changed', newMaterial: MaterialV1): void;
}>();
const expanded = defineModel({
  default: false,
});

const onLanguageChanged = (newMaterial: MaterialV1) => {
  emit('language-changed', newMaterial);
};

const { t } = useLocale();
const { otherCategories } = useMaterialCategories(computed(() => props.material));
</script>

<template>
  <DrawerBottom
    :modelValue="expanded"
    color="surface-container-lowest"
    @close="emit('close')"
    :extra-classes="['pa-0', 'rounded-t-xl']"
    :dialog-label="material.title"
  >
    <MaterialDetails
      :material="material"
      :materials-with-same-original-title="materialsWithSameOriginalTitle as MaterialV1[]"
      :is-download-loading="isDownloadLoading"
      :is-print-loading="isPrintLoading"
      @click-download="emit('click-download')"
      @click-print="emit('click-print')"
      @click-add-to-collection="emit('click-add-to-collection')"
      @language-changed="onLanguageChanged"
    >
      <template v-if="otherCategories.length > 0" #categories>
        <v-chip
          v-for="category in otherCategories"
          :key="category.id"
          class="mr-4 mb-4 rounded bg-tertiary-container"
          color="on-tertiary"
          label
          prepend-icon="fa:fas fa-tag"
          variant="flat"
          role="listitem"
          tag="li"
        >
          {{ t(`${category.category}`) }}
        </v-chip>
      </template>
    </MaterialDetails>
  </DrawerBottom>
</template>

<style scoped lang="scss"></style>
